import { Button, Flex, GridItem, Icon, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { t } from "i18next";
import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  ContactPersonDto,
  ContactSubjectType,
  OrgPublicProfileDto,
  OrganizationEntryDto,
  PersonDto,
  useListContactPersonsQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { canUpdateOrgEntry } from "../../../../common/auth/utils";
import { displayPhoneNumber } from "../../../../common/formatting/displayPhoneNumber";
import { dedupeByEmail } from "../../../../common/contactPersons/supplierContactPersons";
import { displayPersonName } from "../../../contracts/view-single/sharing/AddExternalParticipantModal";
import { ContactKindTag } from "./ContactKindTag";
import { ContactPersonAddForm } from "./ContactPersonAddForm";
import { ContactPersonRow } from "./ContactPersonRow";
import { ContactSubject, organizationEntrySubject, registeredOrganizationSubject } from "./contactSubject";

interface ContactPersonsProps {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
}

type PlatformItem = { kind: "platform"; person: PersonDto; orgName: string };
type EditableItem = { kind: "editable"; person: ContactPersonDto; subject: ContactSubject };

export const ContactPersons = ({ organization, organizationEntry }: ContactPersonsProps) => {
  const authState = useLoggedInWithOrgContextState();
  const [isAdding, setIsAdding] = useState(false);

  const isOwnOrganization = !!organization && organization.id === authState.selectedOrg.id;

  const {
    data: registeredOrgContacts,
    isLoading: isLoadingContacts,
    isError: contactsError,
  } = useListContactPersonsQuery(
    organization && !isOwnOrganization
      ? { subjectType: ContactSubjectType.RegisteredOrganization, subjectId: organization.id }
      : skipToken
  );

  if (!organization && !organizationEntry) return null;

  const platformItems: PlatformItem[] = organization
    ? (organization.contactPersons?.length ? organization.contactPersons : [organization.owner]).map((person) => ({
        kind: "platform",
        person,
        orgName: organization.name,
      }))
    : [];

  const registeredOrgItems: EditableItem[] =
    organization && registeredOrgContacts
      ? registeredOrgContacts.contacts.map((person) => ({
          kind: "editable",
          person,
          subject: registeredOrganizationSubject(organization),
        }))
      : [];

  const linkedEntryItems: EditableItem[] = organization
    ? organization.linkedEntries
        .filter((entry) => entry.contactPersons && entry.contactPersons.length > 0)
        .flatMap((entry) =>
          (entry.contactPersons ?? []).map((person) => ({
            kind: "editable" as const,
            person,
            subject: organizationEntrySubject(entry),
          }))
        )
    : [];

  const entryItems: EditableItem[] = organizationEntry
    ? (organizationEntry.contactPersons ?? []).map((person) => ({
        kind: "editable",
        person,
        subject: organizationEntrySubject(organizationEntry),
      }))
    : [];

  const allContactPersons = dedupeByEmail(
    [...platformItems, ...registeredOrgItems, ...linkedEntryItems, ...entryItems],
    (item) => item.person.email
  );

  const candidateSubjects: ContactSubject[] = [
    ...(organization && !isOwnOrganization ? [registeredOrganizationSubject(organization)] : []),
    ...(organization?.linkedEntries ?? []).map(organizationEntrySubject),
    ...(organizationEntry ? [organizationEntrySubject(organizationEntry)] : []),
  ];

  const canAddContactPerson = canUpdateOrgEntry(authState.selectedOrg.roles) && candidateSubjects.length > 0;

  return (
    <GridItem colSpan={{ base: 1, lg: 2 }}>
      <Flex
        border="1px solid"
        borderColor="smBorder"
        flexDirection="column"
        alignItems="start"
        justifyContent="start"
        borderRadius="lg"
        bg="smBackground"
        width="full"
        maxWidth="full"
        height="auto"
        position="relative"
        p="6"
      >
        <Flex width="full" justifyContent="space-between" alignItems="center" pb="2" gap="2">
          <Text fontSize="md" fontWeight="bold">
            {t("Contact persons")}
          </Text>
          {canAddContactPerson && !isAdding && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              leftIcon={<Icon as={FaPlus} />}
              onClick={() => setIsAdding(true)}
            >
              {t("Add contact person")}
            </Button>
          )}
        </Flex>
        <Flex
          width="full"
          maxWidth="full"
          overflowX="auto"
          overflowY="auto"
          maxHeight="400px"
          border="1px solid"
          borderColor="smBorder"
          rounded="lg"
          pt="5"
          pb="2.5"
          position="relative"
          justifyContent={allContactPersons.length === 0 ? "center" : undefined}
        >
          {allContactPersons.length === 0 && isLoadingContacts ? (
            <Spinner size="sm" color="smMuted" my="2" />
          ) : allContactPersons.length === 0 && contactsError ? (
            <Text color="smMuted" fontSize="sm" py="2">
              {t("Could not load contact persons")}
            </Text>
          ) : allContactPersons.length === 0 ? (
            <Text color="smMuted" fontSize="sm" py="2">
              {t("No contact persons added yet")}
            </Text>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th minWidth="60">{t("Name")}</Th>
                  <Th>{t("Email")}</Th>
                  <Th>{t("Phone Number")}</Th>
                  <Th>{t("Title")}</Th>
                  <Th textAlign="center">{t("Organization")}</Th>
                  <Th textAlign="center">{t("Type")}</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {allContactPersons.map((item) => {
                  if (item.kind === "platform")
                    return (
                      <Tr key={item.person.email}>
                        <Td>{displayPersonName(item.person)}</Td>
                        <Td>{item.person.email}</Td>
                        <Td whiteSpace="nowrap">
                          {item.person.phoneNumber ? displayPhoneNumber(item.person.phoneNumber) : null}
                        </Td>
                        <Td>{item.person.corporateTitle}</Td>
                        <Td textAlign="center">{item.orgName}</Td>
                        <Td textAlign="center">
                          <ContactKindTag kind="withAccount" size="sm" />
                        </Td>
                        <Td></Td>
                      </Tr>
                    );
                  return <ContactPersonRow key={item.person.email} person={item.person} subject={item.subject} />;
                })}
              </Tbody>
            </Table>
          )}
        </Flex>
        {isAdding && <ContactPersonAddForm subjects={candidateSubjects} onClose={() => setIsAdding(false)} />}
      </Flex>
    </GridItem>
  );
};
